///
/// EarthBrowser
///
/// Created by Matt Giger on 2/23/2024
/// Copyright (c) 2024 Geodata Labs
///

import { requestRender } from "@stovokor/engine/RenderRequest"
import { color, rgba } from "@stovokor/math/color"
import { type rect } from "@stovokor/math/rect"
import { type Font } from "@stovokor/uipanes/Font"

export type fillType = string | number | CanvasGradient | CanvasPattern | color | Canvas
export type CanvasState = "normal" | "highlighted" | "disabled" | "selected" | "focused"

export interface shape {
	render(context: OffscreenCanvasRenderingContext2D): void
}

export class Canvas {
	private _width: number = 0
	private _height: number = 0
	private _scale: number = 1
	private _state: CanvasState = "normal"
	private _path?: Path2D
	private _dirty: boolean = true
	protected _canvas!: OffscreenCanvas
	protected _context!: OffscreenCanvasRenderingContext2D

	public get width(): number { return this._width }
	public get height(): number { return this._height }
	public get scale(): number { return this._scale }
	public get canvas(): OffscreenCanvas { return this._canvas }
	public get context(): OffscreenCanvasRenderingContext2D { return this._context }

	public get needsUpdate(): boolean { return this._dirty }
	public setNeedsUpdate(): void { this._needsUpdate = true }

	/**
	 * Accessor rather than a field so subclasses that flag themselves
	 * directly (Label, label overlays) also wake the frame loop: the pane
	 * only re-rasterizes while a frame is being drawn
	 */
	protected get _needsUpdate(): boolean { return this._dirty }
	protected set _needsUpdate(value: boolean) {
		this._dirty = value
		if(value) {
			requestRender()
		}
	}

	public get state(): CanvasState { return this._state }
	public set state(value: CanvasState) {
		if(value !== this._state) {
			this._state = value
			this._needsUpdate = true
		}
	}

	/**
	 * Rasterize the contents. width/height are logical (CSS) pixels, the
	 * coordinate space every drawing call and intrinsicSize() work in; scale
	 * is the device pixel ratio, so the backing surface is scale times
	 * larger and the pane texture stays sharp on HiDPI displays
	 */
	public rerender(width: number, height: number, scale: number): boolean {
		this._needsUpdate = false
		let resized = false
		this._width = width
		this._height = height
		this._scale = scale
		this.prepareSurface(width, height)

		// Give the canvas a chance to ask for a new size (e.g.: for text rendering)
		const size = this.intrinsicSize()
		if(size[0] > this._width || size[1] > this._height) {
			resized = true
			this._width = size[0]
			this._height = size[1]
			this.prepareSurface(this._width, this._height)
		}

		this.render()
		return resized
	}

	/**
	 * Reuse one OffscreenCanvas per Canvas instance instead of allocating a
	 * fresh one on every rerender: browsers (iOS Safari especially) reclaim
	 * discarded canvas backing stores lazily, so frequently-updating panes
	 * would otherwise pile up garbage surfaces. Setting the dimensions
	 * clears the surface and resets all context state, matching the old
	 * new-canvas-per-render semantics; the pixel-ratio transform is
	 * reapplied after every reset so subclasses keep drawing in logical pixels.
	 */
	private prepareSurface(width: number, height: number): void {
		const surfaceWidth = Math.max(1, Math.ceil(width * this._scale))
		const surfaceHeight = Math.max(1, Math.ceil(height * this._scale))
		if(!this._canvas) {
			this._canvas = new OffscreenCanvas(surfaceWidth, surfaceHeight)
			const context = this._canvas.getContext("2d", { willReadFrequently: true })
			if(!context) {
				throw new Error("Failed to create 2D context")
			}
			this._context = context
		} else {
			this._canvas.width = surfaceWidth
			this._canvas.height = surfaceHeight
		}
		if(this._scale !== 1) {
			this._context.scale(this._scale, this._scale)
		}
	}

	/** Device-pixel size of the backing surface (logical size times scale) */
	public get surfaceWidth(): number { return this._canvas.width }
	public get surfaceHeight(): number { return this._canvas.height }

	public get imageData(): ImageData {
		return this._context.getImageData(0, 0, this._canvas.width, this._canvas.height)
	}

	public copyTo(canvas: CanvasRenderingContext2D): void {
		canvas.drawImage(this._canvas, 0, 0, this._width, this._height)
	}

	public render(): void {
		// subclasses should override this method
	}

	// subclasses can override this method to set the intrinsic size
	public intrinsicSize(): [number, number] {
		return [this._width, this._height]
	}

	public clear(): Canvas {
		this._context.clearRect(0, 0, this._width, this._height)
		return this
	}

	public beginPath(): Canvas {
		this._context.beginPath()
		return this
	}

	public closePath(): Canvas {
		this._context.closePath()
		return this
	}

	public moveTo(x: number, y: number): Canvas {
		this._context.moveTo(x, y)
		return this
	}

	public lineTo(x: number, y: number): Canvas {
		this._context.lineTo(x, y)
		return this
	}

	public line(x1: number, y1: number, x2: number, y2: number): Canvas {
		this._context.moveTo(x1, y1)
		this._context.lineTo(x2, y2)
		return this
	}

	public linesTo(...points: number[]): Canvas {
		for(let i = 0; i < points.length-1; i += 2) {
			this.lineTo(points[i], points[i+1])
		}
		return this
	}

	public stroke(color: color | number, lineWidth: number = 1, lineCap: CanvasLineCap = "square", lineJoin: CanvasLineJoin = "round"): Canvas {
		const context = this._context
		if(typeof color === 'number') {
			context.strokeStyle = rgba(color).toString()
		} else {
			context.strokeStyle = color.toString()
		}
		context.lineWidth = lineWidth
		context.lineCap = lineCap
		context.lineJoin = lineJoin
		if(this._path) {
			context.stroke(this._path)
			this._path = undefined
		} else {
			context.stroke()
		}
		return this
	}

	public filter(filter: string): Canvas {
		this._context.filter = filter
		return this
	}

	public innerStroke(color: color | number, lineWidth: number = 1): Canvas {
		const context = this._context
		context.save()
		context.clip()
		context.lineWidth = lineWidth * 2
		if(typeof color === 'number') {
			context.strokeStyle = rgba(color).toString()
		} else {
			context.strokeStyle = color.toString()
		}
		if(this._path) {
			context.stroke(this._path)
			this._path = undefined
		} else {
			context.stroke()
		}
		context.restore()
		return this
	}

	public fill(fillStyle: fillType): Canvas {
		const context = this._context
		if(fillStyle instanceof color) {
			context.fillStyle = fillStyle.toString()
		} else if(typeof fillStyle === 'string') {
			context.fillStyle = fillStyle
		} else if(typeof fillStyle === 'number') {
			context.fillStyle = rgba(fillStyle).toString()
		} else if(fillStyle instanceof CanvasGradient || fillStyle instanceof CanvasPattern) {
			context.fillStyle = fillStyle
		} else if(fillStyle instanceof Canvas) {
			const pattern = fillStyle.pattern()
			if(pattern) {
				context.fillStyle = pattern
			} else {
				throw new Error("Failed to create pattern")
			}
		}
		if(this._path) {
			context.fill(this._path)
			this._path = undefined
		} else {
			context.fill()
		}
		return this
	}

	public gradient(x0: number, y0: number, x1: number, y1: number, colorStops: [number, color][]): CanvasGradient {
		const gradient = this._context.createLinearGradient(x0, y0, x1, y1)
		for(const [offset, color] of colorStops) {
			gradient.addColorStop(offset, color.toString())
		}
		return gradient
	}

	public shadow(color: color | number, blur: number = 4, offsetX: number = 4, offsetY: number = 4): Canvas {
		const context = this._context
		if(typeof color === 'number') {
			context.shadowColor = rgba(color).toString()
		} else {
			context.shadowColor = color.toString()
		}
		// Shadow geometry ignores the context transform, so apply the
		// pixel-ratio scale by hand to keep it in logical pixels
		context.shadowBlur = blur * this._scale
		context.shadowOffsetX = offsetX * this._scale
		context.shadowOffsetY = offsetY * this._scale
		return this
	}

	public pattern(): CanvasPattern | null {
		const pattern = this._context.createPattern(this._canvas, "repeat")
		// The surface holds scale x scale device pixels per logical pixel;
		// counter that so the pattern tiles at its logical size
		if(pattern && this._scale !== 1) {
			pattern.setTransform(new DOMMatrix().scale(1 / this._scale, 1 / this._scale))
		}
		return pattern
	}

	public rect(rect: rect): Canvas {
		this._context.rect(rect.left, rect.top, rect.width, rect.height)
		return this
	}

	public rectangle(x: number, y: number, width: number, height: number, rounding: number = 0): Canvas {
		if(rounding > 0) {
			this._context.roundRect(x, y, width, height, rounding)
		} else {
			this._context.rect(x, y, width, height)
		}
		return this
	}

	public circle(x: number, y: number, radius: number): Canvas {
		this._context.arc(x, y, radius, 0, Math.PI * 2)
		return this
	}

	public arc(x: number, y: number, radius: number, startAngle: number, endAngle: number, anticlockwise: boolean = false): Canvas {
		this._context.arc(x, y, radius, startAngle, endAngle, anticlockwise)
		return this
	}

	public shape(s: shape): Canvas {
		s.render(this._context)
		return this
	}

	public svgPath(svg: string, scale: number = 1): Canvas {
		if(!this._path) {
			this._path = new Path2D()
		}
		this._path.addPath(new Path2D(svg), new DOMMatrix().scale(scale, scale))
		return this
	}

	public measureText(text: string, font: Font, outline: boolean): [number, number] {
		const context = this._context
		context.font = font.toString()
		const metrics = context.measureText(text)
		const width = Math.ceil(metrics.width)
		const height = Math.ceil(metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent)
		const offset = outline ? 2 : 0
		return [width + offset, height + offset]
	}

	public drawText(text: string, font: Font, color: color, x: number = 0, y: number = 0, outline?: color): Canvas {
		const context = this._context
		context.font = font.toString()
		context.textAlign = "left"
		context.textBaseline = "top"
		// context.textRendering = "geometricPrecision"

		if(outline) {
			context.fillStyle = outline.toString()
			context.fillText(text, x, y)
			context.fillText(text, x + 2, y)
			context.fillText(text, x, y + 2)
			context.fillText(text, x + 2, y + 2)
			context.fillStyle = color.toString()
			context.fillText(text, x + 1, y + 1)
		} else {
			context.fillStyle = color.toString()
			context.fillText(text, x, y)
		}
		return this
	}
}

// two dimensional rotation operation
function rotate2d(x: number, y: number, angle: number): [number, number] {
	const cos = Math.cos(angle)
	const sin = Math.sin(angle)
	return [x * cos - y * sin, x * sin + y * cos]
}

export class triangle implements shape {
	public length: number
	public angle: number

	constructor(length: number, angle: number = 0) {
		this.length = length
		this.angle = angle
	}

	render(context: OffscreenCanvasRenderingContext2D): void {
		const angle = this.angle * Math.PI / 180
		const centerLen = Math.floor(length * Math.sqrt(3) / 3)
		const [x1, y1] = rotate2d(centerLen, 0, angle)
		const [x2, y2] = rotate2d(centerLen, 0, angle + 2 * Math.PI / 3)
		const [x3, y3] = rotate2d(centerLen, 0, angle + 4 * Math.PI / 3)
		context.moveTo(x1 + centerLen, y1 + centerLen)
		context.lineTo(x2 + centerLen, y2 + centerLen)
		context.lineTo(x3 + centerLen, y3 + centerLen)
		context.closePath()
	}
}

export class polygon implements shape {
	public sides: number
	public size: number
	public center: [number, number]
	public angle: number

	constructor(sides: number, size: number, center: [number, number], angle: number = 0) {
		this.sides = sides
		this.size = size
		this.center = center
		this.angle = angle
	}

	render(context: OffscreenCanvasRenderingContext2D): void {
		const angle = this.angle * Math.PI / 180
		context.translate(this.center[0], this.center[1])
		context.rotate(angle)
		context.beginPath()
		context.moveTo(this.size, 0)
		for(let i = 1; i < this.sides; i++) {
			const x = this.size * Math.cos(2 * Math.PI * i / this.sides)
			const y = this.size * Math.sin(2 * Math.PI * i / this.sides)
			context.lineTo(x, y)
		}
		context.closePath()
		context.rotate(-angle)
		context.translate(-this.center[0], -this.center[1])
	}
}